import { Link, useLocation } from "react-router-dom";
import { Facebook, Phone, MapPin } from "lucide-react";
import encoreLogo from "@/assets/encore-logo.png";
import skillMammothLogo from "@/assets/skillmammoth-logo.png";

const services = [
  { name: "Service Panels", path: "/services/service-panels" },
  { name: "EV Chargers", path: "/services/ev-chargers" },
  { name: "Electrical Remodeling", path: "/services/electrical-remodeling" },
  { name: "Commercial Services", path: "/services/commercial" },
  { name: "Outdoor Lights", path: "/services/outdoor-lights" },
];

const quickLinks = [
  { name: "About Us", path: "/about" },
  { name: "Projects", path: "/projects" },
  { name: "Blog", path: "/blog" },
  { name: "Contact", path: "/contact" },
];

const locations = [
  { name: "Maple Grove", path: "/locations/maple-grove" },
  { name: "Plymouth", path: "/locations/plymouth" },
  { name: "Minneapolis", path: "/locations/minneapolis" },
  { name: "Brooklyn Park", path: "/locations/brooklyn-park" },
  { name: "Eden Prairie", path: "/locations/eden-prairie" },
];

export const Footer = () => {
  const location = useLocation();
  const isHomePage = location.pathname === "/";
  return (
    <footer className="bg-footer text-footer-foreground" role="contentinfo">
      <div className="container mx-auto px-4 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
          {/* Logo & About */}
          <div className="space-y-6">
            <Link to="/" aria-label="Encore Electric - Home">
              <img 
                src={encoreLogo} 
                alt="Encore Electric logo" 
                className="h-16 w-auto bg-white p-2 rounded-lg" 
                width={160}
                height={64}
                loading="lazy"
                decoding="async"
              />
            </Link>
            <div className="text-sm text-footer-foreground/80 space-y-1">
              <p className="font-semibold text-footer-foreground">Contractor No. EA807204</p>
              <p>Licensed Master Electrician</p>
              <p>Licensed, Bonded, and Insured</p>
            </div>
            <p className="text-footer-foreground/80 leading-relaxed">
              Professional electrical services for residential and commercial properties across the Twin Cities metro area.
            </p>
            <div className="flex gap-4">
              <a
                href="https://www.facebook.com/"
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 rounded-full bg-secondary/20 flex items-center justify-center hover:bg-secondary transition-colors"
                aria-label="Visit Encore Electric on Facebook"
              >
                <Facebook className="w-5 h-5" aria-hidden="true" />
              </a>
            </div>
          </div>

          {/* Services */}
          <nav aria-label="Services">
            <h2 className="text-lg font-heading font-semibold text-primary-foreground mb-6">Our Services</h2>
            <ul className="space-y-3">
              {services.map((service) => (
                <li key={service.path}>
                  <Link to={service.path} className="text-footer-foreground/80 hover:text-secondary transition-colors">
                    {service.name}
                  </Link>
                </li>
              ))}
            </ul>
          </nav>

          {/* Quick Links */}
          <nav aria-label="Quick Links">
            <h2 className="text-lg font-heading font-semibold text-primary-foreground mb-6">Quick Links</h2>
            <ul className="space-y-3">
              {quickLinks.map((link) => (
                <li key={link.path}>
                  <Link to={link.path} className="text-footer-foreground/80 hover:text-secondary transition-colors">
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </nav>

          {/* Contact Info */}
          <div>
            <h2 className="text-lg font-heading font-semibold text-primary-foreground mb-6">Contact Us</h2>
            <address className="not-italic space-y-4 text-footer-foreground/80">
              <div className="flex items-center gap-2">
                <Phone className="w-4 h-4 text-secondary" aria-hidden="true" />
                <a href="tel:+19525000074" className="hover:text-secondary transition-colors" aria-label="Call Encore Electric at (952) 500-0074">
                  (952) 500-0074
                </a>
              </div>
              <div className="space-y-2">
                <span className="block font-medium text-primary-foreground flex items-center gap-2">
                  <MapPin className="w-4 h-4 text-secondary" aria-hidden="true" />
                  Service Areas
                </span>
                <ul className="space-y-1 pl-6 text-sm">
                  {locations.map((loc) => (
                    <li key={loc.name}>
                      <Link to={loc.path} className="hover:text-secondary transition-colors">{loc.name}, MN</Link>
                    </li>
                  ))}
                </ul>
              </div>
            </address>
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="border-t border-primary-foreground/10">
        <div className="container mx-auto px-4 py-6">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <p className="text-footer-foreground/60 text-sm text-center md:text-left">
              © 2026 Encore Electric. All rights reserved.
            </p>
            <a
              href="https://skillmammoth.com/"
              target="_blank"
              rel={isHomePage ? "noopener noreferrer" : "noopener noreferrer nofollow"}
              className="flex items-center gap-2 text-footer-foreground/60 hover:text-footer-foreground transition-colors text-sm"
            >
              Site Design & SEO by Skill Mammoth Digital
              <img src={skillMammothLogo} alt="Skill Mammoth Digital logo" className="h-5 w-5" loading="lazy" />
            </a>
            <div className="flex gap-4 text-sm">
              <Link to="/privacy-policy" className="text-footer-foreground/60 hover:text-secondary transition-colors">Privacy Policy</Link>
              <Link to="/terms" className="text-footer-foreground/60 hover:text-secondary transition-colors">Terms of Use</Link>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};
